/**
 * The coach's derived readings, in one leaf module.
 *
 * These helpers answer questions BOTH the chrome and the canvas ask — which cues
 * are on the board, in what order, whether the coach is allowed on screen at
 * all, and which of its surfaces belongs there. They lived in `Coach.tsx` and
 * the canvas imported them from
 * there, which closed a `canvas → ui → canvas` loop: `Coach.tsx` calls
 * `useGraphFrame`, and `useGraphFrame` needed the order. The loop was inert, but
 * it was a loop.
 *
 * So the answers moved here and the components stayed there. This file imports
 * NOTHING from `canvas/` and nothing from `Coach.tsx`, and it renders nothing —
 * no JSX — so the remaining edge is one-way.
 *
 * Prices: `coachExtrasOf` is the only place the coach meets money, and it hands
 * the pure layer PLAIN NUMBERS to compare — the figure a person reads still
 * comes from `VerbPrice`, the one arithmetic every other price in this app goes
 * through. Nothing here formats a dollar.
 */

import {
  activeEntry,
  missingKeyNeed,
  providerFor,
  resolveAuditor,
  resolveFor,
  useStore,
} from '../core/store'
import { CUE_ACTION_PAID, CUE_PRICE_VERB, type Cue, type CueActionKind } from '../core/cues'
import { coachCues, freshCoach, type CoachExtras, type CoachState } from '../core/coach'
import { PROVIDERS, type ModelEntry, type Role } from '../core/models'
import { asUpNextVerb, upNextRole } from '../core/upNext'
import type { Run } from '../core/types'
import { STATUS_WORD, pressEstimate } from '../core/vocabulary'
import { hasDemoReserve } from '../engine/demo'
import { projectVerbUsd } from '../engine/run'
import { voiceOn } from '../core/voice'
import { searchlessCheckSentence } from '../core/corpus'
import { PRIOR_ART_NEED } from '../engine/reason'
import { restingPlan, restingRefusal } from './restingPress'
import { missingKeyLine, reasonKeyLine } from './pressName'

/** The store's shape, without exporting `State` from the store for it. */
export type StoreState = ReturnType<typeof useStore.getState>

/**
 * The coach, but only if it belongs to the session on screen.
 *
 * `CoachState` carries the id of the exploration it was opened over, and every
 * surface here reads the coach through this rather than off the store, so a
 * coach left over from another document cannot put a card, a pin or a number on
 * this one. The cues under it are computed from `s.session` — a foreign coach
 * would be retirement state about nodes that are not here, applied to nodes that
 * are.
 *
 * Both known seams are already closed at the source: a fork clears the coach,
 * and the demo's remint re-tags it with the new id (`store.ts`, `beginOp`). This
 * is the standing defense behind them, so a future path that changes a session's
 * id — or swaps the document under a held coach — degrades to showing nothing
 * rather than to showing another exploration's reading of this one.
 */
export function liveCoach(s: StoreState): CoachState | null {
  const c = s.coach
  return c && c.sessionId === s.session.id ? c : null
}

/** One reference, so a coach-less state cannot make a subscriber re-render on
 *  every unrelated write. */
export const NO_CUES: Cue[] = []

/**
 * The four prices the pure layer is allowed to compare, and the two budget
 * figures the `decide` gate reads.
 *
 * Memoized on the routing view and the source run's record — the only inputs —
 * so the object is reference-stable and `coachCues`' own memo can hit. That
 * matters: the selector runs inside a zustand subscription that fires on every
 * store write.
 *
 * An audit's price is another house's and cannot be derived from the routing
 * view, so `projectVerbUsd` returns null here and the key is simply absent. The
 * ordering input for a cross-examination is therefore zero, and the FIGURE the
 * reader sees comes from `VerbPrice` with the auditor resolved the way the
 * inspector resolves it. A missing key beats a wrong one.
 */
let extrasMemo: {
  modelId: StoreState['modelId']
  roleModels: StoreState['roleModels']
  sourceRun: Run | null
  /** The graph and the ceiling, for the one press priced as a run: how many claims
   *  it names is a fact about the graph. `coachCues` compares the table by value,
   *  so a rebuilt object carrying the same figures still hits its memo. */
  session: StoreState['session']
  runCeiling: StoreState['runCeiling']
  /** Every figure in the table includes the voice's line while it is on (D295). */
  voice: boolean
  value: CoachExtras
} | null = null

/** The run the coach is speaking about, resolved once for the memo key. Through
 *  `liveCoach`, like every other reading here: a run id from another session
 *  must not be looked up in this session's list. */
function sourceRunOf(s: StoreState): Run | null {
  const id = liveCoach(s)?.sourceRunId
  return id ? (s.session.runs.find((r) => r.id === id) ?? null) : null
}

export function coachExtrasOf(s: StoreState): CoachExtras {
  const sourceRun = sourceRunOf(s)
  if (
    extrasMemo &&
    extrasMemo.modelId === s.modelId &&
    extrasMemo.roleModels === s.roleModels &&
    extrasMemo.sourceRun === sourceRun &&
    extrasMemo.session === s.session &&
    extrasMemo.runCeiling === s.runCeiling &&
    extrasMemo.voice === voiceOn()
  ) {
    return extrasMemo.value
  }

  const route = { modelId: s.modelId, roleModels: s.roleModels }
  const usdByAction: Partial<Record<CueActionKind, number>> = {}
  for (const [kind, verb] of Object.entries(CUE_PRICE_VERB) as [
    CueActionKind,
    (typeof CUE_PRICE_VERB)[CueActionKind],
  ][]) {
    if (!verb) continue
    const usd = projectVerbUsd(route, verb)
    if (usd !== null) usdByAction[kind] = usd
  }
  // The press priced as a run rather than a verb, through the same plan its button
  // prints — so the CHEAPEST lens sorts on the figure the reader sees.
  const resting = restingPlan(s).est
  if (resting !== null) usdByAction.checkResting = resting

  // The RUN's own ceiling against the RUN's own draw — both stored at the close.
  // `budget.usd` is the figure the user typed for that run and `estimatedUsd` is
  // the one-time subtraction the account keeps, so their difference is what the
  // ending actually left under the cap. The composer's CURRENT ceiling and the
  // whole-session ledger are the wrong scopes here: a run's cap binds against
  // what that run drew (see `drawnUsd` in `engine/run.ts`), and a session has
  // usually spent plenty before the run this coach is speaking about — read
  // against the pooled ledger, a $5 cap over a $10 exploration would claim the
  // ceiling was crossed before the run took its first move. "Left" still only
  // means something against a limit somebody set: a run with no `budget.usd`
  // yields null and the `decide` arm simply does not fire.
  const capLeftUsd =
    sourceRun && sourceRun.budget.usd != null && sourceRun.estimatedUsd != null
      ? sourceRun.budget.usd - sourceRun.estimatedUsd
      : null
  const value: CoachExtras = {
    usdByAction,
    capLeftUsd,
    // A move the run would make if it went on: quiet, as a run's moves are, so
    // the pass alone — with the voice's line in it, "the ceiling will not cover
    // another move" would fire where one would still fit (D295).
    nextMoveUsd: projectVerbUsd(route, 'move', null, { voice: false }),
  }
  extrasMemo = {
    modelId: s.modelId,
    roleModels: s.roleModels,
    sourceRun,
    session: s.session,
    runCeiling: s.runCeiling,
    voice: voiceOn(),
    value,
  }
  return value
}

/** The visible cues, in the order every coach surface must agree on. */
export function visibleCues(s: StoreState): Cue[] {
  const coach = liveCoach(s)
  if (!coach) return NO_CUES
  return coachCues(s.session, coach, s.focusId, coachExtrasOf(s))
}

/**
 * At rest, at the latest step.
 *
 * The transport's own definition of "now" — `playhead === null || playhead >=
 * order.length`, the predicate the space bar and the dock's play button already
 * read (`App.tsx`, `Ledger.tsx`) — plus the gate `openCoach` applies: a moving
 * graph already has an author of attention. Scrubbing back hides the card, the
 * bar and the keyboard together, because a cue about the live graph read beside
 * a rewound one is a claim about a document that is not on screen.
 */
export function coachGateOpen(s: StoreState): boolean {
  return (
    !s.busy &&
    // Any un-ended run, matching `openCoach`'s door — the two disagreeing is
    // how a held bar resurfaces where the door would refuse to open one. A
    // run WAITING at its confirm gate is included on purpose: the pause is a
    // question on screen, and the previous run's bar, pins and digest rising
    // over it would compete with the one thing being asked.
    (s.run === null || s.run.phase === 'ended') &&
    (s.playhead === null || s.playhead >= s.session.order.length)
  )
}

/**
 * The three composites a surface actually mounts on, written once.
 *
 * They were copied into the components and into the test, which is exactly how
 * the pins came to outlive their own chrome: Escape's last rung puts the bar
 * away, and a `show` that never asked about the bar left numbered marks on a
 * graph with nothing left on screen to explain them. So the predicates live
 * here, with the gate they already depend on, and there is one answer to give.
 *
 * `bar || open` rather than `bar` alone: the pins stand down when EVERY coach
 * surface has been put away, and the card can be reopened without the bar (the
 * digest's own toggle does it). The digest replaces the pins rather than joining
 * them — two readings of one list at once is the crowding this layer removes.
 */
export function pinsShow(s: StoreState): boolean {
  const c = liveCoach(s)
  return !!c && (c.bar || c.open) && !c.digest && coachGateOpen(s) && visibleCues(s).length > 0
}

export function digestShows(s: StoreState): boolean {
  return !!liveCoach(s)?.digest && coachGateOpen(s) && visibleCues(s).length > 0
}

/**
 * The card's own arm, which the card no longer owns alone.
 *
 * It was an inline `gate && coach.open && cues.length > 0` inside the component
 * for as long as the component was the only reader of it. The card is a popup on
 * the graph now, with a second form that mounts in the run-done column at phone
 * width — so the bar has to know whether that row exists before it decides
 * whether to render a column at all, and a second copy of the reading is exactly
 * how the pins outlived their chrome. One answer, beside the other two.
 *
 * No `bar` arm, deliberately, unlike the pins: `closeCoachBar` closes the card
 * with it and `openCoach` raises both, so a card up over a dismissed bar is not
 * a state the store can be in. Asking anyway would be a condition that cannot
 * be false pretending to be a guard.
 */
export function cardShows(s: StoreState): boolean {
  return !!liveCoach(s)?.open && coachGateOpen(s) && visibleCues(s).length > 0
}

/**
 * The coach nobody has opened yet: nothing retired, nothing on screen, no run
 * behind it — exactly what `openCoachDigest` builds when nothing is held, so the
 * line can count the board that press will list. One object per exploration id,
 * because `coachCues` memoizes on the coach's identity and a fresh literal per
 * store write would assemble the whole board on every one of them.
 */
let resting: CoachState | null = null
export function restingCoach(sessionId: string): CoachState {
  if (resting?.sessionId !== sessionId) {
    resting = { ...freshCoach(sessionId, null, null), open: false, bar: false }
  }
  return resting
}

/**
 * How many things the coach's quiet line counts — or 0, and no line at all.
 *
 * The coach no longer opens itself after a pass: a seed's opening used to raise
 * the card over the ring it had just landed. What it has to say waits here
 * instead, as a count, until somebody asks for the list. So this reads the held
 * coach when there is one — its retirements are what the list will honour — and
 * the resting board when there is none, and it says nothing wherever every other
 * coach surface is hidden (`coachGateOpen`, and `openCoach`'s floor of a seed with
 * something grown under it).
 *
 * It counts while a run's close holds the coach up as well. The run-done bar used
 * to carry this count itself and the line stood down beside it; the bar folded
 * into the run strip above the input, which carries the RUN's ending and nothing
 * of the coach's, so the line is the one count at every moment rather than the
 * second of two.
 */
export function coachLineCount(s: StoreState): number {
  if (!coachGateOpen(s) || s.session.order.length < 2) return 0
  const c = liveCoach(s)
  if (c) return visibleCues(s).length
  return coachCues(s.session, restingCoach(s.session.id), s.focusId, coachExtrasOf(s)).length
}

/**
 * The card's heading, from the record rather than from which door opened it.
 *
 * "After the run" is a claim — that what the card reads is the graph a run just
 * left — and it had been printed over a seed's opening, a single pass that was
 * never a run. It is true only when the coach is speaking about a finished run
 * AND no pass has started since that run ended: press Deepen after a run and the
 * board the card reads is no longer the one the run left. Everything else is
 * "What to look at next", which claims nothing about where the graph came from. A
 * narration is not a pass (D121), so a run's own wrap-up does not flip it.
 */
export function coachHeading(s: StoreState): string {
  const c = liveCoach(s)
  const run = c?.sourceRunId ? s.session.runs.find((r) => r.id === c.sourceRunId) : undefined
  const ended = run?.finishedAt
  if (ended === undefined) return STATUS_WORD.worthALook
  const since = s.session.ops.some((op) => !op.narration && op.startedAt > ended)
  return since ? STATUS_WORD.worthALook : STATUS_WORD.afterRun
}

/**
 * The other house a cross-examination would be handed to, or null.
 *
 * One reading, for the same reason `visibleCues` is one reading: the card
 * DISABLES its button on a null auditor, and a keyboard that resolved it
 * separately — or not at all — would make `Enter` mean something the mouse
 * cannot do. Resolved exactly the way the inspector's own cross-examination
 * control resolves it (`Grounding.tsx`, `AuditButton`): the provider that wrote
 * the claim, falling back to the active entry's for a node with no stamp.
 *
 * Narrow input rather than the whole store, so the card can pass the five
 * fields it already subscribes to field by field and stay reactive.
 */
type AuditView = Pick<StoreState, 'session' | 'modelId' | 'keys' | 'customBase' | 'customModel'>
export function cueAuditor(s: AuditView, cue: Cue): ReturnType<typeof resolveAuditor> {
  if (cue.action?.kind !== 'audit') return null
  const against = s.session.nodes[cue.anchorId]?.model?.provider ?? activeEntry(s).provider
  const { modelId, keys, customBase, customModel } = s
  return resolveAuditor({ modelId, keys, customBase, customModel }, against)
}

/** Everything a refusal is decided from: the routing view, the document, the one
 *  preference that changes what a source check can run against — and, for the one
 *  press that starts a run, the ceiling that run gets and the locks it would meet. */
type RefusalView = AuditView &
  Pick<StoreState, 'roleModels' | 'preferCorpus' | 'runCeiling' | 'run' | 'busy'>

/**
 * Why this cue's action cannot run at all, in the sentence the reader gets — or
 * null when it can.
 *
 * The generalization of the audit's own pre-check, and the reason it had to be
 * generalized: `takeCueAction` retires the instance and reports `cue_acted`
 * before it dispatches, so an action that refuses DOWNSTREAM spends the cue on a
 * pass that never ran. Verify, deepen, challenge and the link scan can all
 * refuse exactly as the audit can — a routed role with no key refuses and never
 * falls back to the selection (invariant 4), and a model with no web search
 * cannot run a check whose whole subject is the live record.
 *
 * So the same questions the engine asks in its own guards are asked HERE, before
 * anything is retired, from the same functions: `resolveFor` for a routed role,
 * `resolveAuditor` for the other house, `entry.search` for the capability. What
 * is duplicated is the sentence, never the decision — and the card disables its
 * button on this same reading, so the button, `Enter` and the digest's row
 * cannot come to disagree about whether a cue is pressable.
 *
 * Deliberately NOT asked here: `busy` (the gate every coach surface renders
 * behind already means no pass is in flight), the seed (`anchorable` in
 * `cues.ts` never anchors a cue on the root, so a check on the seed is not
 * reachable), and whether the user's own documents happen to address the claim
 * (`runVerify`'s corpus arm) — that is a fact about the CONTENT, discovered by
 * retrieving, and a press that gets that far has really been made.
 */
export function cueRefusal(s: RefusalView, cue: Cue): string | null {
  const action = cue.action
  if (!action) return null
  switch (action.kind) {
    case 'audit':
      // `runAudit`'s own condition — a house other than the one that wrote the
      // claim, with a key. Its second guard (a branch entirely written by the
      // only other model you hold a key for) is left to the engine: it reads the
      // branch, and a cue whose anchor cannot be examined at all is rare enough
      // that saying so at the press is honest.
      return cueAuditor(s, cue)
        ? null
        : 'A cross-examination needs a key for a second provider — one from the same company would be the panel again.'
    case 'verify': {
      const m = resolveFor(s, 'check')
      if (!m) {
        return `A source check searches the live web, so it needs your ${providerFor(s, 'check').name} key.`
      }
      // "Prefer my sources" is the one thing that makes a searchless model
      // usable for this pass, so it is read here exactly as `runVerify` reads
      // it — otherwise the card would refuse a press the engine would honour.
      if (m.entry.search === 'none' && !s.preferCorpus) {
        return searchlessCheckSentence(m.entry.name)
      }
      return null
    }
    case 'linkScan': {
      const m = resolveFor(s, 'check')
      if (!m) {
        return `A link scan searches the live web, so it needs your ${providerFor(s, 'check').name} key.`
      }
      // No corpus arm on this one, deliberately, and `runLinkScan` says why: the
      // test is over what a WEB search did not return.
      if (m.entry.search === 'none') {
        return `${m.entry.name} has no web search, so there is no record for a link scan to come back empty from.`
      }
      return null
    }
    case 'priorArt': {
      // `runPriorArt`'s own two refusals, in its own order: no key, then no search.
      // The key's in D202's sentence, the one its key card says when the pass
      // refuses (`PRIOR_ART_NEED`) — which names the route's model, and asks a
      // custom endpoint for its address rather than a key (D259, D286).
      const m = resolveFor(s, 'check')
      if (!m) {
        const { provider, need } = missingKeyNeed(s, 'check', PRIOR_ART_NEED)
        return missingKeyLine(PROVIDERS[provider], need)
      }
      if (m.entry.search === 'none') {
        return `${m.entry.name} has no web search, so there is no record for a prior-art search to come back empty from.`
      }
      return null
    }
    case 'conjecture':
      // No demo arm: a pre-authored proposal is what this pass exists not to
      // produce, so `runConjecture` refuses without a key wherever it is pressed.
      // A reasoning pass, so the reasoning route's line, as Deepen's below.
      return resolveFor(s, 'reason') ? null : reasonKeyLine(s)
    case 'deepen':
    case 'challenge': {
      if (resolveFor(s, 'reason')) return null
      // The demo's pre-authored branches unfurl with no key at all, which is the
      // whole point of it — `runOp` reveals one instead of refusing — so a cue
      // over a reserved branch is pressable and must not be greyed out.
      if (hasDemoReserve(cue.anchorId, action.kind)) return null
      // Not `runOp`'s toast, which names the demo exploration — true where it
      // fires from the demo and false for a cue anchored anywhere else — but the
      // key card's line that same refusal opens: the reasoning route's model and
      // its house (`reasonKeyLine`, D202), the promote row's sentence too. The
      // CONDITION above is quoted exactly.
      return reasonKeyLine(s)
    }
    // A checks-only run: its own reading, because it is `startRun`'s refusals that
    // apply to it — a live run, the ceiling — as well as the check role's.
    case 'checkResting':
      return restingRefusal(s)
    // The free five. `record` opens a form, `prune` is a local edit, `inspect`
    // is a selection, `report` is a tab and `group` writes overrides into the
    // Lab's ledger: nothing to resolve and nothing to refuse.
    case 'record':
    case 'prune':
    case 'inspect':
    case 'report':
    case 'group':
      return null
  }
}

/**
 * The route a paid cue's pass runs on. Every press that waits on the Up next line
 * answers through the line's own table (`upNextRole`), so a cue and the button it
 * stands for cannot come to read two routes: the prior-art search was read on the
 * reasoning route here while it searched on the check route (D286). The link scan
 * and the checks-only run are not on the line, and both search as a check does.
 */
const cueRole = (kind: CueActionKind): Role => {
  const verb = asUpNextVerb(kind)
  return verb ? upNextRole(verb) : 'check'
}

/**
 * What a cue's press says after its words, in its accessible name: the estimate
 * this one press adds, or the status word that stands where a figure cannot —
 * the chips' own reading (`ActRow` in `ui/Feed.tsx`), asked of a cue.
 *
 * The same questions `cueRefusal` asks, and in the same order the chips ask them:
 * a press that runs no model says so; one with no key for its role says it needs
 * one — or, over the worked example's authored branch, that it is part of the
 * demo and spends nothing; everything else is priced through `projectVerbUsd`,
 * the arithmetic `VerbPrice` prints. A paid press with no figure (a link scan, a
 * model with no list rate) says nothing rather than a wrong number.
 *
 * The auditor comes in resolved (`cueAuditor`), as the card and the digest
 * already hold it, so the name and the figure are one arithmetic on one model.
 */
type TailView = Pick<StoreState, 'modelId' | 'roleModels' | 'keys' | 'customBase' | 'customModel'>
export function cuePressTail(s: TailView, cue: Cue, auditor: ModelEntry | null): string | null {
  const action = cue.action
  if (!action) return null
  if (!CUE_ACTION_PAID[action.kind]) return STATUS_WORD.free.toLowerCase()
  const keyless = action.kind === 'audit' ? auditor === null : !resolveFor(s, cueRole(action.kind))
  if (keyless) {
    const reserved =
      (action.kind === 'deepen' || action.kind === 'challenge') &&
      hasDemoReserve(cue.anchorId, action.kind)
    return (reserved ? STATUS_WORD.demo : STATUS_WORD.needsKey).toLowerCase()
  }
  const verb = CUE_PRICE_VERB[action.kind]
  const est = verb
    ? projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, verb, auditor)
    : null
  return est !== null ? pressEstimate(est) : null
}

/**
 * A cue action's full accessible name, or undefined where its visible words
 * already are one.
 *
 * The button's own label leads, so the name holds what a voice-control user sees
 * and says ("Challenge it"); then the claim it is aimed at, which the button never
 * printed; then `cuePressTail`. Two presses are left to their text: the run
 * account names no node, and the checks-only run's label already counts its
 * claims and prints its estimate in so many words.
 */
export function cuePressName(cue: Cue, tail: string | null): string | undefined {
  const action = cue.action
  if (!action || action.kind === 'report' || action.kind === 'checkResting') return undefined
  return `${action.label}: “${cue.title}”${tail ? ` · ${tail}` : ''}`
}

/** Subscriptions for the readings above, so a surface re-renders exactly when the
 *  answer changes. Every selector is identity-stable by construction — the two
 *  newest return a number and a string, which compare by value. */
export function useVisibleCues(): Cue[] {
  return useStore(visibleCues)
}
export function useLiveCoach(): CoachState | null {
  return useStore(liveCoach)
}
export function useCoachGate(): boolean {
  return useStore(coachGateOpen)
}
export function usePinsShow(): boolean {
  return useStore(pinsShow)
}
export function useDigestShows(): boolean {
  return useStore(digestShows)
}
export function useCardShows(): boolean {
  return useStore(cardShows)
}
export function useCoachLineCount(): number {
  return useStore(coachLineCount)
}
export function useCoachHeading(): string {
  return useStore(coachHeading)
}
